import React, { useEffect } from 'react';
import { useBooking, Service as BookingService } from '../../contexts/booking/BookingContext';
import { useServices, Service } from '../../contexts/services/ServiceContext';
import { 
  getNumericPrice, 
  getNumericDuration, 
  getTotalPrice, 
  formatPrice,
  normalizeService
} from '../../utils/bookingUtils';

// We can use the ServiceCategory type from ServiceContext directly

const ServiceSelection: React.FC = () => {
  const { bookingState, addService, removeService, nextStep } = useBooking();
  const { selectedServices } = bookingState;
  
  const { categories, isLoading, error } = useServices();

  // Check for pre-selected service from localStorage
  useEffect(() => {
    const loadSavedService = () => {
      const savedService = localStorage.getItem('selectedService');
      if (!savedService) return;
      
      try {
        const parsedService = JSON.parse(savedService);
        if (parsedService && typeof parsedService === 'object') {
          // Normalize the service before adding
          const normalizedService = normalizeService(parsedService);
          addService(normalizedService);
        } else {
          console.error('Invalid service format in localStorage');
        }
        
        // Remove from localStorage to avoid duplicates on refresh
        localStorage.removeItem('selectedService');
      } catch (err) {
        console.error('Error parsing saved service:', err);
      }
    };
    
    loadSavedService();
  }, [addService]);
  
  // No need to fetch service data - it's managed by ServiceContext

  // Check if a service is selected
  const isServiceSelected = (serviceId: string) => {
    return selectedServices.some(service => service.id === serviceId);
  };

  // Toggle service selection
  const toggleService = (service: Service) => {
    if (isServiceSelected(service.id)) {
      removeService(service.id);
    } else {
      addService(service);
    }
  };

  // Handle "Continue" button click
  const handleContinue = () => {
    if (selectedServices.length > 0) {
      nextStep();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B2D8C5]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="bg-[#B2D8C5] text-white px-4 py-2 rounded-md"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-6">Select Your Services</h2>
      
      {/* Selected services summary */}
      {selectedServices.length > 0 && (
        <div className="mb-6 bg-[#F1F7F4] p-4 rounded-md">
          <h3 className="text-lg font-medium text-[#232347] mb-2">Selected Services:</h3>
          <ul className="space-y-2">
            {selectedServices.map(service => (
              <li key={service.id} className="flex justify-between items-center">
                <span>{service.name}</span>
                <div className="flex items-center">
                  <span className="text-sm text-gray-600 mr-4">
                    <span className="font-medium text-red-500">${formatPrice(getNumericPrice(service))}</span> • {getNumericDuration(service)} min
                  </span>
                  <button 
                    onClick={() => removeService(service.id)}
                    className="text-red-500 hover:text-red-700"
                    aria-label="Remove service"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 text-right text-lg font-medium">
            Total: ${formatPrice(getTotalPrice(selectedServices))}
          </div>
        </div>
      )}
      
      {/* Categories and services */}
      {categories.map(category => (
        <div key={category.id} className="mb-8">
          <h3 className="text-xl font-serif text-[#232347] mb-4">{category.name || category.title}</h3>
          <p className="text-gray-600 mb-4">{category.description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {category.services.map(service => (
              <div 
                key={service.id} 
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  isServiceSelected(service.id) 
                    ? 'border-[#B2D8C5] bg-[#F1F7F4]' 
                    : 'border-gray-200 hover:border-[#B2D8C5]'
                }`}
                onClick={() => toggleService(service)}
              >
                <div className="flex justify-between items-start">
                  <h4 className="text-lg font-medium text-[#232347]">{service.name}</h4>
                  <div className="flex items-center">
                    {isServiceSelected(service.id) ? (
                      <div className="bg-[#B2D8C5] rounded-full p-1 text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    ) : (
                      <div className="border border-gray-300 rounded-full p-1 text-transparent">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-gray-600 text-sm mt-2">{service.short_description || service.description}</p>
                <div className="flex justify-between mt-4 text-sm">
                  <span>{getNumericDuration(service)} minutes</span>
                  <div>
                    {service.discountedPrice ? (
                      <div className="flex flex-col items-end">
                        <span className="text-xs line-through text-gray-400">${formatPrice(service.numericPrice || 0)}</span>
                        <span className="font-bold text-red-500">${formatPrice(parseFloat(service.discountedPrice.toString().replace(/[^0-9.]/g, '')))}</span>
                      </div>
                    ) : (
                      <span className="font-medium">${formatPrice(getNumericPrice(service))}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      
      {/* Continue button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleContinue}
          disabled={selectedServices.length === 0}
          className={`px-6 py-2 rounded-md text-white ${
            selectedServices.length > 0
              ? 'bg-[#B2D8C5] hover:bg-[#88bfa3]'
              : 'bg-gray-300 cursor-not-allowed'
          } transition-colors`}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default ServiceSelection;
